import { debugWarn } from '@shared/utils/Logger';

/**
 * Audio Manager - Handles all game audio
 *
 * Features:
 * - Background music with crossfade
 * - Sound effects with pooling
 * - Volume controls (master, music, sfx)
 * - Positional audio (distance-based volume)
 */

export type SoundCategory = 'music' | 'sfx' | 'ui' | 'ambient';

export interface SoundConfig {
  id: string;
  url: string;
  category: SoundCategory;
  volume?: number;      // 0-1, default 1
  loop?: boolean;       // default false
  poolSize?: number;    // Number of instances for pooling (sfx only)
}

export interface PlayOptions {
  volume?: number;      // Override default volume
  loop?: boolean;       // Override default loop
  position?: { x: number; y: number }; // World position for 3D sound
  listenerPos?: { x: number; y: number }; // Listener (player) position
  maxDistance?: number; // Max distance for positional audio
}

// Sound instance with state tracking
interface SoundInstance {
  audio: HTMLAudioElement;
  isPlaying: boolean;
}

// Sound pool for frequently played sounds
interface SoundPool {
  instances: SoundInstance[];
  currentIndex: number;
  config: SoundConfig;
}

export class AudioManager {
  private static instance: AudioManager;

  // Volume settings (0-1)
  private masterVolume: number = 0.7;
  private musicVolume: number = 0.5;
  private sfxVolume: number = 0.8;
  private uiVolume: number = 0.6;
  private ambientVolume: number = 0.4;

  // Sound pools
  private soundPools: Map<string, SoundPool> = new Map();

  // Currently playing music
  private currentMusic: HTMLAudioElement | null = null;
  private musicId: string | null = null;

  // Currently playing ambient sounds
  private currentAmbientSounds: HTMLAudioElement[] = [];
  private currentAmbientIds: string[] = [];

  // Audio context for advanced features
  private audioContext: AudioContext | null = null;

  // Mute state
  private isMuted: boolean = false;

  private constructor() {
    // Try to create AudioContext (may need user interaction first)
    try {
      this.audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      debugWarn('AudioManager',' AudioContext not supported');
    }
  }

  static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  /**
   * Register a sound for use
   */
  registerSound(config: SoundConfig): void {
    const poolSize = config.poolSize || (config.category === 'sfx' ? 3 : 1);

    const instances: SoundInstance[] = [];
    for (let i = 0; i < poolSize; i++) {
      const audio = new Audio(config.url);
      audio.volume = this.calculateVolume(config.category, config.volume || 1);
      audio.loop = config.loop || false;
      audio.preload = 'auto';

      // Handle audio errors gracefully
      audio.onerror = () => {
        debugWarn('AudioManager',` Failed to load sound: ${config.id}`);
      };

      instances.push({ audio, isPlaying: false });
    }

    this.soundPools.set(config.id, {
      instances,
      currentIndex: 0,
      config,
    });
  }

  /**
   * Register multiple sounds at once
   */
  registerSounds(configs: SoundConfig[]): void {
    for (const config of configs) {
      this.registerSound(config);
    }
  }

  /**
   * Play a sound effect
   */
  play(soundId: string, options: PlayOptions = {}): HTMLAudioElement | null {
    if (this.isMuted) return null;

    const pool = this.soundPools.get(soundId);
    if (!pool) {
      debugWarn('AudioManager',` Sound not found: ${soundId}`);
      return null;
    }

    // Get next available instance from pool
    const instance = this.getAvailableInstance(pool);
    if (!instance) return null;

    const audio = instance.audio;

    // Calculate volume
    let volume = options.volume ?? pool.config.volume ?? 1;

    // Apply positional audio
    if (options.position && options.listenerPos) {
      const dx = options.position.x - options.listenerPos.x;
      const dy = options.position.y - options.listenerPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const maxDist = options.maxDistance || 500;
      const distanceFactor = Math.max(0, 1 - distance / maxDist);
      volume *= distanceFactor;
    }

    audio.volume = this.calculateVolume(pool.config.category, volume);
    audio.loop = options.loop ?? pool.config.loop ?? false;

    // Reset and play
    audio.currentTime = 0;
    instance.isPlaying = true;

    audio.play().catch(() => {
      // Autoplay might be blocked - this is expected
      instance.isPlaying = false;
    });

    audio.onended = () => {
      instance.isPlaying = false;
    };

    return audio;
  }

  /**
   * Play background music
   */
  playMusic(soundId: string, fadeIn: boolean = true): void {
    if (this.musicId === soundId) return; // Already playing

    const pool = this.soundPools.get(soundId);
    if (!pool) {
      debugWarn('AudioManager',` Music not found: ${soundId}`);
      return;
    }

    // Fade out current music and mark old instance as not playing
    if (this.currentMusic && this.musicId) {
      const oldPool = this.soundPools.get(this.musicId);
      const oldInstance = oldPool?.instances[0];
      this.fadeOut(this.currentMusic, 1000, () => {
        if (oldInstance) oldInstance.isPlaying = false;
      });
    }

    // Get first instance for music (only need one)
    const instance = pool.instances[0];
    this.currentMusic = instance.audio;
    this.musicId = soundId;

    instance.audio.loop = true;
    instance.audio.volume = 0;
    instance.isPlaying = true;

    instance.audio.play().catch(() => {
      // Autoplay blocked - music will start on user interaction
      instance.isPlaying = false;
    });

    if (fadeIn) {
      this.fadeIn(instance.audio, 2000, this.calculateVolume('music', pool.config.volume || 1));
    } else {
      instance.audio.volume = this.calculateVolume('music', pool.config.volume || 1);
    }
  }

  /**
   * Play zone-specific music with crossfade
   */
  playZoneMusic(zoneId: string): void {
    const mapping = ZONE_MUSIC_MAP[zoneId];
    if (!mapping) return;

    // If it's the same track, just adjust playback rate
    if (this.musicId === mapping.trackId && this.currentMusic) {
      if (mapping.playbackRate) {
        this.currentMusic.playbackRate = mapping.playbackRate;
      } else {
        this.currentMusic.playbackRate = 1.0;
      }
    } else {
      // Play new track
      this.playMusic(mapping.trackId);

      // Adjust playback rate after music starts
      if (mapping.playbackRate && this.currentMusic) {
        this.currentMusic.playbackRate = mapping.playbackRate;
      }
    }

    // Play zone ambient sounds
    const ambientConfig = ZONE_AMBIENT_MAP[zoneId];
    if (ambientConfig) {
      this.playZoneAmbient(ambientConfig);
    } else {
      this.stopAmbient();
    }
  }

  /**
   * Play ambient sound loops for a zone
   */
  private playZoneAmbient(soundIds: string[]): void {
    // Stop current ambient sounds
    this.stopAmbient();

    for (const soundId of soundIds) {
      const pool = this.soundPools.get(soundId);
      if (!pool) continue;

      const instance = pool.instances[0];
      if (!instance) continue;

      instance.audio.loop = true;
      instance.audio.volume = this.calculateVolume('ambient', pool.config.volume || 1);
      instance.isPlaying = true;

      instance.audio.play().catch(() => {
        instance.isPlaying = false;
      });

      this.currentAmbientSounds.push(instance.audio);
      this.currentAmbientIds.push(soundId);
    }
  }

  /**
   * Stop all ambient sounds with fade out
   */
  stopAmbient(): void {
    for (const audio of this.currentAmbientSounds) {
      this.fadeOut(audio, 800);
    }
    // Mark old instances as not playing
    for (const id of this.currentAmbientIds) {
      const pool = this.soundPools.get(id);
      if (pool) {
        for (const inst of pool.instances) inst.isPlaying = false;
      }
    }
    this.currentAmbientSounds = [];
    this.currentAmbientIds = [];
  }

  /**
   * Stop background music
   */
  stopMusic(fadeOut: boolean = true): void {
    if (!this.currentMusic) return;

    if (fadeOut) {
      this.fadeOut(this.currentMusic, 1000);
    } else {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
    }

    this.currentMusic = null;
    this.musicId = null;
  }

  /**
   * Stop a specific sound
   */
  stop(soundId: string): void {
    const pool = this.soundPools.get(soundId);
    if (!pool) return;

    for (const instance of pool.instances) {
      instance.audio.pause();
      instance.audio.currentTime = 0;
      instance.isPlaying = false;
    }
  }

  /**
   * Stop all sounds
   */
  stopAll(): void {
    for (const pool of this.soundPools.values()) {
      for (const instance of pool.instances) {
        instance.audio.pause();
        instance.audio.currentTime = 0;
        instance.isPlaying = false;
      }
    }
    this.currentMusic = null;
    this.musicId = null;
    this.currentAmbientSounds = [];
    this.currentAmbientIds = [];
  }

  // Volume controls
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  setUiVolume(volume: number): void {
    this.uiVolume = Math.max(0, Math.min(1, volume));
    this.updateAllVolumes();
  }

  getMasterVolume(): number { return this.masterVolume; }
  getMusicVolume(): number { return this.musicVolume; }
  getSfxVolume(): number { return this.sfxVolume; }

  // Mute controls
  mute(): void {
    this.isMuted = true;
    this.updateAllVolumes();
  }

  unmute(): void {
    this.isMuted = false;
    this.updateAllVolumes();
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.updateAllVolumes();
  }

  isMutedState(): boolean {
    return this.isMuted;
  }

  // Resume audio context (needed after user interaction)
  resumeContext(): void {
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  // Private helper methods
  private getAvailableInstance(pool: SoundPool): SoundInstance | null {
    // Find first non-playing instance
    for (const instance of pool.instances) {
      if (!instance.isPlaying) {
        return instance;
      }
    }

    // All instances busy - use round-robin
    const instance = pool.instances[pool.currentIndex];
    pool.currentIndex = (pool.currentIndex + 1) % pool.instances.length;

    // Stop current playback
    instance.audio.pause();
    instance.audio.currentTime = 0;

    return instance;
  }

  private calculateVolume(category: SoundCategory, soundVolume: number): number {
    if (this.isMuted) return 0;

    let categoryVolume: number;
    switch (category) {
      case 'music': categoryVolume = this.musicVolume; break;
      case 'sfx': categoryVolume = this.sfxVolume; break;
      case 'ui': categoryVolume = this.uiVolume; break;
      case 'ambient': categoryVolume = this.ambientVolume; break;
      default: categoryVolume = 1;
    }

    return this.masterVolume * categoryVolume * soundVolume;
  }

  private updateAllVolumes(): void {
    for (const pool of this.soundPools.values()) {
      const newVolume = this.calculateVolume(pool.config.category, pool.config.volume || 1);
      for (const instance of pool.instances) {
        if (instance.isPlaying) {
          instance.audio.volume = newVolume;
        }
      }
    }
  }

  private fadeIn(audio: HTMLAudioElement, duration: number, targetVolume: number): void {
    const startTime = Date.now();
    const startVolume = 0;

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(1, elapsed / duration);

      audio.volume = startVolume + (targetVolume - startVolume) * progress;

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    tick();
  }

  private fadeOut(audio: HTMLAudioElement, duration: number, onComplete?: () => void): void {
    const startTime = Date.now();
    const startVolume = audio.volume;

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(1, elapsed / duration);

      audio.volume = startVolume * (1 - progress);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        audio.pause();
        audio.currentTime = 0;
        onComplete?.();
      }
    };

    tick();
  }
}

// Zone music mapping
const ZONE_MUSIC_MAP: Record<string, { trackId: string; playbackRate?: number }> = {
  // Act 1
  otag_koyu: { trackId: 'music_gameplay', playbackRate: 0.9 },
  karanlik_orman: { trackId: 'music_gameplay' },
  ruh_batakligi: { trackId: 'music_gameplay', playbackRate: 0.85 },
  erlik_tapinagi: { trackId: 'music_boss', playbackRate: 0.9 },
  // Act 2
  ergenekon_girisi: { trackId: 'music_gameplay', playbackRate: 1.05 },
  firtina_ovasi: { trackId: 'music_gameplay', playbackRate: 1.1 },
  ates_magarasi: { trackId: 'music_boss', playbackRate: 0.8 },
  yersub_tahti: { trackId: 'music_boss', playbackRate: 0.85 },
  // Act 3
  karanlik_tunel: { trackId: 'music_gameplay', playbackRate: 0.75 },
  olum_nehri: { trackId: 'music_gameplay', playbackRate: 0.8 },
  kemik_ormani: { trackId: 'music_gameplay', playbackRate: 0.85 },
  lanetli_saray: { trackId: 'music_boss', playbackRate: 0.85 },
  // Act 4
  erlik_kapisi: { trackId: 'music_boss', playbackRate: 0.9 },
  cehennem_bahcesi: { trackId: 'music_boss', playbackRate: 0.95 },
  taht_odasi: { trackId: 'music_boss' },
};

// Zone ambient sound mapping
const ZONE_AMBIENT_MAP: Record<string, string[]> = {
  // Act 1
  otag_koyu: ['ambient_wind_light'],
  karanlik_orman: ['ambient_wind', 'ambient_forest'],
  ruh_batakligi: ['ambient_water', 'ambient_whispers'],
  erlik_tapinagi: ['ambient_fire', 'ambient_drums'],
  // Act 2
  ergenekon_girisi: ['ambient_wind_strong', 'ambient_rocks'],
  firtina_ovasi: ['ambient_wind_strong', 'ambient_thunder'],
  ates_magarasi: ['ambient_fire', 'ambient_lava'],
  yersub_tahti: ['ambient_water', 'ambient_earth'],
  // Act 3
  karanlik_tunel: ['ambient_drip', 'ambient_echoes'],
  olum_nehri: ['ambient_water', 'ambient_whispers'],
  kemik_ormani: ['ambient_wind', 'ambient_bones'],
  lanetli_saray: ['ambient_whispers', 'ambient_thunder'],
  // Act 4
  erlik_kapisi: ['ambient_fire', 'ambient_drums'],
  cehennem_bahcesi: ['ambient_fire', 'ambient_whispers'],
  taht_odasi: ['ambient_thunder', 'ambient_drums'],
};

// Predefined game sounds
export const GAME_SOUNDS: SoundConfig[] = [
  // UI Sounds
  { id: 'ui_click', url: '/audio/ui/click_001.ogg', category: 'ui', poolSize: 2 },
  { id: 'ui_hover', url: '/audio/ui/select_001.ogg', category: 'ui', poolSize: 2, volume: 0.5 },
  { id: 'ui_open', url: '/audio/ui/open_001.ogg', category: 'ui' },
  { id: 'ui_close', url: '/audio/ui/close_001.ogg', category: 'ui' },
  { id: 'ui_confirm', url: '/audio/ui/confirmation_001.ogg', category: 'ui' },
  { id: 'ui_error', url: '/audio/ui/error_001.ogg', category: 'ui' },
  { id: 'ui_level_up', url: '/audio/ui/maximize_008.ogg', category: 'ui' },

  // Combat sounds - Player attacks
  { id: 'hit_physical', url: '/audio/combat/impactPunch_heavy_000.ogg', category: 'sfx', poolSize: 5 },
  { id: 'hit_physical_1', url: '/audio/combat/impactPunch_heavy_001.ogg', category: 'sfx', poolSize: 3 },
  { id: 'hit_physical_2', url: '/audio/combat/impactPunch_heavy_002.ogg', category: 'sfx', poolSize: 3 },
  { id: 'hit_critical', url: '/audio/combat/impactPunch_heavy_004.ogg', category: 'sfx', poolSize: 3 },
  { id: 'hit_soft', url: '/audio/combat/impactSoft_medium_000.ogg', category: 'sfx', poolSize: 4 },
  { id: 'hit_soft_1', url: '/audio/combat/impactSoft_medium_001.ogg', category: 'sfx', poolSize: 3 },

  // Combat sounds - Metal/Weapon
  { id: 'hit_metal', url: '/audio/combat/impactMetal_heavy_000.ogg', category: 'sfx', poolSize: 3 },
  { id: 'hit_metal_1', url: '/audio/combat/impactMetal_heavy_001.ogg', category: 'sfx', poolSize: 3 },
  { id: 'block', url: '/audio/combat/impactPlate_heavy_000.ogg', category: 'sfx', poolSize: 2 },
  { id: 'slash', url: '/audio/combat/impactGeneric_light_000.ogg', category: 'sfx', poolSize: 4 },
  { id: 'slash_1', url: '/audio/combat/impactGeneric_light_001.ogg', category: 'sfx', poolSize: 3 },

  // Combat sounds - Wood (for staff/bow)
  { id: 'hit_wood', url: '/audio/combat/impactWood_medium_000.ogg', category: 'sfx', poolSize: 3 },
  { id: 'hit_wood_1', url: '/audio/combat/impactWood_medium_001.ogg', category: 'sfx', poolSize: 3 },

  // Enemy hit/death
  { id: 'enemy_hit', url: '/audio/combat/impactSoft_heavy_000.ogg', category: 'sfx', poolSize: 5 },
  { id: 'enemy_hit_1', url: '/audio/combat/impactSoft_heavy_001.ogg', category: 'sfx', poolSize: 3 },
  { id: 'enemy_death', url: '/audio/combat/impactSoft_heavy_004.ogg', category: 'sfx', poolSize: 3 },

  // Footsteps
  { id: 'footstep_grass', url: '/audio/combat/footstep_grass_000.ogg', category: 'sfx', poolSize: 5, volume: 0.4 },
  { id: 'footstep_grass_1', url: '/audio/combat/footstep_grass_001.ogg', category: 'sfx', poolSize: 3, volume: 0.4 },
  { id: 'footstep_grass_2', url: '/audio/combat/footstep_grass_002.ogg', category: 'sfx', poolSize: 3, volume: 0.4 },

  // Item sounds
  { id: 'item_pickup', url: '/audio/ui/drop_001.ogg', category: 'sfx', poolSize: 3 },
  { id: 'item_equip', url: '/audio/combat/impactPlate_light_000.ogg', category: 'sfx' },
  { id: 'item_drop', url: '/audio/ui/drop_003.ogg', category: 'sfx', poolSize: 3 },
  { id: 'gold_pickup', url: '/audio/ui/confirmation_002.ogg', category: 'sfx', poolSize: 4, volume: 0.6 },

  // Music
  { id: 'music_menu', url: '/audio/music/Vampir.mp3', category: 'music', loop: true },
  { id: 'music_gameplay', url: '/audio/music/Aksiyon.mp3', category: 'music', loop: true },
  { id: 'music_boss', url: '/audio/music/Aksiyon.mp3', category: 'music', loop: true },

  // Ambient sounds (looping, low volume)
  { id: 'ambient_wind', url: '/audio/combat/footstep_concrete_000.ogg', category: 'ambient', loop: true, volume: 0.3 },
  { id: 'ambient_wind_light', url: '/audio/combat/footstep_concrete_001.ogg', category: 'ambient', loop: true, volume: 0.2 },
  { id: 'ambient_wind_strong', url: '/audio/combat/footstep_concrete_002.ogg', category: 'ambient', loop: true, volume: 0.4 },
  { id: 'ambient_forest', url: '/audio/combat/footstep_grass_003.ogg', category: 'ambient', loop: true, volume: 0.25 },
  { id: 'ambient_water', url: '/audio/combat/footstep_snow_000.ogg', category: 'ambient', loop: true, volume: 0.3 },
  { id: 'ambient_fire', url: '/audio/combat/impactPlank_medium_000.ogg', category: 'ambient', loop: true, volume: 0.2 },
  { id: 'ambient_whispers', url: '/audio/combat/impactSoft_medium_002.ogg', category: 'ambient', loop: true, volume: 0.15 },
  { id: 'ambient_drums', url: '/audio/combat/impactPunch_medium_000.ogg', category: 'ambient', loop: true, volume: 0.2 },
  { id: 'ambient_thunder', url: '/audio/combat/impactPunch_heavy_003.ogg', category: 'ambient', loop: true, volume: 0.25 },
  { id: 'ambient_drip', url: '/audio/combat/footstep_snow_001.ogg', category: 'ambient', loop: true, volume: 0.2 },
  { id: 'ambient_echoes', url: '/audio/combat/impactGlass_light_000.ogg', category: 'ambient', loop: true, volume: 0.15 },
  { id: 'ambient_lava', url: '/audio/combat/impactPlank_medium_001.ogg', category: 'ambient', loop: true, volume: 0.2 },
  { id: 'ambient_earth', url: '/audio/combat/impactMining_000.ogg', category: 'ambient', loop: true, volume: 0.2 },
  { id: 'ambient_rocks', url: '/audio/combat/impactMining_001.ogg', category: 'ambient', loop: true, volume: 0.15 },
  { id: 'ambient_bones', url: '/audio/combat/impactWood_medium_002.ogg', category: 'ambient', loop: true, volume: 0.15 },
];

// Convenience function to get the audio manager
export function getAudioManager(): AudioManager {
  return AudioManager.getInstance();
}
